import { useState } from "react";
import { Upload, CheckCircle2, AlertTriangle } from "lucide-react";
import { API } from "@/lib/api";
import { apiFetch } from "@/lib/api";
import { toast } from "sonner";

export const SpreadsheetImport = ({ onImported }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setPreview(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await fetch(`${API}/import/preview`, { method: "POST", credentials: "include", body: form });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Could not read that file");
      setPreview(await res.json());
    } catch (err) {
      toast.error(err.message);
    }
    setBusy(false);
    e.target.value = "";
  };

  const commit = async () => {
    setBusy(true);
    try {
      const res = await apiFetch("/import/commit", {
        method: "POST",
        body: JSON.stringify({ events: preview.new }),
      });
      toast.success(`${res.inserted} new date(s) added · ${res.skipped_existing} already on the calendar`);
      setPreview(null);
      onImported?.();
    } catch (err) {
      toast.error(err.message);
    }
    setBusy(false);
  };

  return (
    <section className="mt-14 border-t border-neutral-200 pt-10" data-testid="import-section">
      <h2 className="text-2xl font-bold tracking-tight text-neutral-900">Import a calendar spreadsheet</h2>
      <p className="mt-1 text-sm text-neutral-500">
        Upload an .xlsx file — you'll see a preview first. Existing dates and RSVPs are never touched.
      </p>

      <label className="mt-5 inline-flex cursor-pointer items-center gap-2 rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50">
        <Upload className="h-4 w-4" /> {busy ? "Reading…" : "Choose spreadsheet"}
        <input type="file" accept=".xlsx,.xlsm" onChange={upload} className="hidden" data-testid="import-file-input" />
      </label>

      {preview && (
        <div className="mt-6" data-testid="import-preview">
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1.5 font-semibold text-neutral-800">
              <CheckCircle2 className="h-4 w-4 text-[#008A05]" /> {preview.new.length} new dates
            </span>
            <span className="rounded-full bg-neutral-100 px-3 py-1.5 font-semibold text-neutral-600">
              {preview.duplicate_count} already on the calendar
            </span>
            {preview.skipped?.length > 0 && (
              <span className="flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1.5 font-semibold text-neutral-600">
                <AlertTriangle className="h-4 w-4 text-amber-500" /> {preview.skipped.length} rows without a date
              </span>
            )}
          </div>

          {preview.unmapped_columns?.length > 0 && (
            <p className="mt-3 text-xs text-neutral-500">
              Columns I ignored: {preview.unmapped_columns.join(", ")}
            </p>
          )}

          <ul className="mt-5 max-h-72 divide-y divide-neutral-200 overflow-auto rounded-xl border border-neutral-200">
            {preview.new.slice(0, 60).map((e, i) => (
              <li key={i} className="flex items-center gap-3 px-4 py-2.5" data-testid={`import-row-${i}`}>
                <img src={e.image_url} alt="" className="h-9 w-12 rounded-md object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-neutral-900">{e.title}</p>
                  <p className="text-xs text-neutral-500">
                    {new Date(e.start_at).toLocaleString()} · {e.venue_name} · {e.category}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-5 flex gap-3">
            <button
              onClick={commit}
              disabled={busy || preview.new.length === 0}
              data-testid="import-confirm-btn"
              className="rounded-full bg-[#B8FF2E] px-6 py-3 text-sm font-bold text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B] disabled:opacity-60"
            >
              Merge {preview.new.length} new date(s)
            </button>
            <button
              onClick={() => setPreview(null)}
              data-testid="import-cancel-btn"
              className="rounded-full border border-neutral-300 px-5 py-3 text-sm font-semibold text-neutral-700 transition-colors duration-200 hover:bg-neutral-50"
            >
              Discard
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
